import { useState, useEffect } from 'react';
import { View } from 'react-native';
import { css } from '@emotion/native';
import { Text } from '@rneui/themed';
import { RouteProp } from '@react-navigation/native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Buffer } from 'buffer';

import MultilineTextInput from '../../components/MultilineTextInput';
import Container from '../../components/Container';
import withHeader from '../../hocs/withHeader';
import withKeyboardAware from '../../hocs/withKeyboardAware';
import { connectDropdownAlert, Alert } from '../../hocs/withDropdownAlert';
import { Colors, isIOS } from '../../utils/constants';
import { getLength } from '../../utils/fns';
import saveOneOnOneNote from '../../mutations/saveOneOnOneNote';
import { RootStackParamList } from '../../components/Navigation/types';

type Props = {
  isKeyboardOpen: boolean;
  keyboardHeight: number;
  dropdownAlert: Alert;
  route: RouteProp<RootStackParamList, 'Shared Notes'>;
};

function SharedNoteView(props: Props) {
  const { meetingId, note, optimisticSharedNoteUpdate } = props.route.params;

  const [value, setValue] = useState<string>(note ?? '');

  const { isKeyboardOpen, keyboardHeight, dropdownAlert } = props;

  let input;

  useEffect(() => {
    const timer = setTimeout(() => {
      input.focus();
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Container
      flex={1}
      align="flex-start"
      justify="flex-start"
      style={css`
        padding: 20px;
        height: 100%;
        background-color: white;
      `}
    >
      <Container
        flex={1}
        align="flex-start"
        justify="flex-start"
        style={css`
          width: 100%;
          height: 100%;
        `}
      >
        <MultilineTextInput
          getInputRef={i => {
            input = i;
          }}
          onChange={newValue => {
            setValue(newValue);
          }}
          value={value}
          placeholder="Write a shared note"
          onBlur={() => {
            if (value === note) return;

            optimisticSharedNoteUpdate(value);

            saveOneOnOneNote.commit(
              {
                // TODO: Find a better way of doing this
                meetingId: Buffer.from(`OneOnOneMeeting:${meetingId}`).toString('base64'),
                note: value,
              },
              ({ saveOneOnOneNote }) => {
                if (getLength(saveOneOnOneNote.errors)) {
                  dropdownAlert({
                    type: 'error',
                    title: 'Whoops, something is not right',
                    body: 'There was an error saving this shared note, please try again',
                  });
                }
              },
              () => {
                dropdownAlert({
                  type: 'error',
                  title: 'Whoops, something is not right',
                  body: 'There was an error saving this shared note, please try again',
                });
              },
            );
          }}
          blurOnSubmit={false}
          returnKeyType={null}
          editable
          inputStyle={fullSizeStart}
          containerStyle={fullSizeStart}
          inputContainerStyle={fullSizeStart}
        />
      </Container>
      {isKeyboardOpen && (
        <View
          style={{
            marginTop: 10,
            alignItems: 'center',
            width: '100%',
            flexDirection: 'row',
            marginBottom: isIOS ? keyboardHeight : 0,
          }}
        >
          <Ionicons name="md-eye" size={16} color={Colors.darkgray} />
          <Text style={{ marginLeft: 10, color: Colors.darkgray }}>
            After sharing, this is visible to both of you
          </Text>
        </View>
      )}
    </Container>
  );
}

const fullSizeStart = css`
  color: black;
  height: 100%;
  width: 100%;
  align-items: flex-start;
  display: flex;
  justify-content: flex-start;
`;

export default connectDropdownAlert(
  withHeader({ title: 'Shared Notes', mode: 'BACK' })(withKeyboardAware(SharedNoteView)),
);
